import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";

interface ActiveAccountResponse {
  valid: boolean;
  profile: Profile;
}

type Profile = {
  first_name: string;
  last_name: string;
  email: string;
  user_id: string;
  status: string;
};

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [token, setToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const showErrorAndRedirect = (title: string, description: string) => {
    toast({
      title,
      description,
      variant: "destructive",
    });
    setTimeout(() => navigate("/"), 3000);
  };

  const handleTokenValidation = async (tokenToValidate: string) => {
    try {
      setIsLoading(true);
      const { data, error } =
        await supabase.functions.invoke<ActiveAccountResponse>(
          "activate-account",
          {
            body: { token: tokenToValidate, action: "validate" },
          },
        );

      if (error) throw new Error(error.message);

      if (data?.valid) {
        setProfile(data.profile);
        setTokenValid(true);
      } else {
        showErrorAndRedirect(
          "Token inválido",
          "El token de invitación no es válido o ha expirado",
        );
      }
    } catch (error) {
      showErrorAndRedirect(
        "Error de validación",
        "No se pudo validar el token de invitación",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const urlToken = searchParams.get("token");

    if (!urlToken) {
      showErrorAndRedirect(
        "Error",
        "Token de invitación no encontrado en la URL",
      );
      return;
    }

    setToken(urlToken);
    handleTokenValidation(urlToken);
  }, [searchParams, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 8) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 8 caracteres",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);

      const { error } = await supabase.functions.invoke("complete-activation", {
        body: {
          token: token,
          password: formData.password,
        },
      });

      if (error) throw error;

      toast({
        title: "¡Cuenta activada!",
        description:
          "Tu cuenta ha sido activada exitosamente. Serás redirigido al login.",
      });

      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      toast({
        title: "Error de activación",
        description: error.message || "No se pudo activar la cuenta",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <h2 className="text-xl font-semibold mb-2">
              Validando invitación...
            </h2>
            <p className="text-gray-600">
              Por favor espera mientras verificamos tu token de invitación.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-8 h-8 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold mb-2 text-red-700">
              Token inválido
            </h2>
            <p className="text-gray-600 mb-4">
              El token de invitación no es válido o ha expirado. Serás
              redirigido a la página principal.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="text-2xl font-bold text-primary mb-2">
            Estancia Corta
          </div>
          <CardTitle className="text-xl">Activa tu cuenta</CardTitle>
          <p className="text-gray-600">
            Hola {profile?.first_name}, crea tu contraseña para acceder al
            sistema.
          </p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email:</Label>
              <Input
                type="email"
                value={profile?.email || ""}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label htmlFor="password">Nueva contraseña:</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  required
                  minLength={8}
                  placeholder="Mínimo 8 caracteres"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-500" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmar contraseña:</Label>
              <Input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange("confirmPassword", e.target.value)
                }
                required
                placeholder="Confirma tu contraseña"
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activando cuenta...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Activar cuenta
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivateAccount;
